/**
 * Costs sub-tab — Blooph application attribution + untagged resources (Stage 7 Phase 2).
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { getApiBaseUrl } from '@/api/baseUrl';
import { ApiError, getAppCostDetail, getCostsByApp, getUntaggedResources } from '@/api/client';
import { POLL_INTERVAL_COSTS_MS, useRefresh } from '@/context/RefreshContext';
import type {
  AllocationBreakdown,
  AppCostDetail,
  AppCostDetailResponse,
  CostByApp,
  CostsByAppResponse,
  SharedCostAttributionContext,
  UntaggedResource,
  UntaggedResourcesResponse,
} from '@/types/api';
import { formatShortDate, formatWhen } from './costsTabFormat';
import { formatCurrency } from './costsTabPie';
import {
  formatUntaggedRegionCell,
  getUntaggedNameDisplay,
  parseUntaggedResourceId,
  sortedUntaggedTagEntries,
} from './untaggedResourceDisplay';

function formatApiErrorBody(body: unknown): string | null {
  if (body === undefined || body === null) {
    return null;
  }
  if (typeof body !== 'string') {
    try {
      return JSON.stringify(body);
    } catch {
      return Object.prototype.toString.call(body);
    }
  }
  const t = body.trim();
  if (t === '') {
    return null;
  }
  try {
    const o = JSON.parse(t) as { message?: unknown; error?: unknown };
    const msg = o.message ?? o.error;
    if (typeof msg === 'string' && msg.trim() !== '') {
      return msg.trim();
    }
  } catch {
    /* not JSON */
  }
  const max = 500;
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

function appendPhase2FetchHints(err: Error, lines: string[]): void {
  if (err.name !== 'TypeError') {
    return;
  }
  const m = err.message.trim().toLowerCase();
  const looksLikeFetchFailure =
    m === 'failed to fetch' ||
    m === 'load failed' ||
    m.includes('failed to fetch') ||
    m.includes('load failed') ||
    m.includes('networkerror');
  if (!looksLikeFetchFailure) {
    return;
  }
  lines.push(
    'Hint: Confirm observability-api Stage 7 Phase 2 deploy wires GET /costs/by-app and GET /costs/untagged (CORS, JWT authorizer, Lambda routes) similar to GET /costs.',
  );
}

function formatPhase2LoadError(err: unknown, context: string): string {
  const lines: string[] = [];
  try {
    lines.push(`API base: ${getApiBaseUrl()}`);
  } catch (configErr: unknown) {
    lines.push(
      `Configuration: ${configErr instanceof Error ? configErr.message : String(configErr)}`,
    );
  }
  lines.push(`Request: ${context}`);

  if (err instanceof ApiError) {
    lines.push(`HTTP status: ${err.status}`);
    lines.push(`Details: ${err.message}`);
    const bodyText = formatApiErrorBody(err.body);
    if (bodyText !== null) {
      lines.push(`Response body: ${bodyText}`);
    }
    if (err.status === 401) {
      lines.push(
        'Hint: Session may have expired — try logging out and signing in again, then open the Costs tab.',
      );
    } else if (err.status === 403) {
      lines.push('Hint: Your account may not be authorized to access cost data from the API.');
    } else if (err.status === 404) {
      lines.push(
        'Hint: Phase 2 costs routes may be missing on this API (deploy observability-api with /costs/by-app and /costs/untagged).',
      );
    } else if (err.status === 503) {
      lines.push('Hint: Cost Explorer or the costs Lambda may be unavailable; retry later.');
    }
    return lines.join('\n');
  }

  if (err instanceof Error) {
    lines.push(`Client error (${err.name}): ${err.message}`);
    appendPhase2FetchHints(err, lines);
    return lines.join('\n');
  }

  lines.push(`Unknown error: ${String(err)}`);
  return lines.join('\n');
}

function sortAppsByTotal(rows: CostByApp[]): CostByApp[] {
  return [...rows].sort((a, b) => b.totalCost - a.totalCost);
}

function panelDomId(appKey: string): string {
  return `costs-app-detail-${encodeURIComponent(appKey).replace(/%/g, '')}`;
}

function appDisplayName(key: string): string {
  if (key === 'untagged') {
    return 'Untagged';
  }
  return key;
}

function DedicatedAppResourcesSection(props: { month: AppCostDetail; currency: string }): JSX.Element | null {
  const { month, currency } = props;
  const rows = month.dedicatedResources;
  if (rows === undefined) {
    return null;
  }

  return (
    <section className="costs-app-dedicated-resources" aria-label="Dedicated tagged resources">
      <h4 className="costs-detail-subheading">Dedicated resources</h4>
      {rows.length === 0 ? (
        <p className="costs-detail-empty">No dedicated resource rows for this period.</p>
      ) : (
        <div className="costs-table-wrap">
          <table className="costs-phase2-table costs-detail-subtable">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">AWS service</th>
                <th scope="col" className="costs-table-num">
                  Cost (MTD)
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const parsed = parseUntaggedResourceId(r.resourceId);
                const nameDisp = getUntaggedNameDisplay(r.resourceId, parsed, undefined);
                const primaryTitle =
                  nameDisp.hint === null && nameDisp.hintDetail !== null ? nameDisp.hintDetail : undefined;
                return (
                  <tr key={`${r.resourceId}-${r.awsService}`}>
                    <td>
                      <div className="costs-untagged-name" title={primaryTitle}>
                        {nameDisp.primary}
                      </div>
                      {nameDisp.hint !== null ? (
                        <p className="costs-untagged-hint" title={nameDisp.hintDetail ?? nameDisp.hint}>
                          {nameDisp.hint}
                        </p>
                      ) : null}
                      {parsed.consoleUrl !== null ? (
                        <a
                          className="costs-untagged-console-link"
                          href={parsed.consoleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open ↗
                        </a>
                      ) : null}
                    </td>
                    <td>{r.awsService}</td>
                    <td className="costs-table-num">{formatCurrency(r.monthlyCost, r.currency || currency)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CopyUntaggedResourceIdButton({ resourceId }: { resourceId: string }): JSX.Element {
  const [label, setLabel] = useState<'Copy ID' | 'Copied' | 'Failed'>('Copy ID');

  const onCopy = useCallback((): void => {
    void (async (): Promise<void> => {
      try {
        await navigator.clipboard.writeText(resourceId);
        setLabel('Copied');
        window.setTimeout(() => {
          setLabel('Copy ID');
        }, 2000);
      } catch {
        setLabel('Failed');
        window.setTimeout(() => {
          setLabel('Copy ID');
        }, 2000);
      }
    })();
  }, [resourceId]);

  return (
    <button type="button" className="costs-copy-id-btn" onClick={onCopy}>
      {label}
    </button>
  );
}

function AppCostDetailPanel(props: {
  summaryRow: CostByApp;
  detailResp: AppCostDetailResponse | undefined;
  loading: boolean;
  errorText: string | undefined;
  onRetry: () => void;
}): JSX.Element {
  const { summaryRow, detailResp, loading, errorText, onRetry } = props;
  const panelId = panelDomId(summaryRow.app);
  const month = detailResp?.currentMonth;

  if (loading) {
    return (
      <div className="costs-app-detail-panel" id={panelId} role="region" aria-busy="true">
        <p className="costs-detail-skeleton-label">Loading drill-down…</p>
        <div className="costs-detail-skeleton-block" aria-hidden />
        <div className="costs-detail-skeleton-block costs-detail-skeleton-short" aria-hidden />
      </div>
    );
  }

  if (errorText !== undefined) {
    return (
      <div className="costs-app-detail-panel" id={panelId} role="region">
        <div className="costs-detail-error" role="alert">
          <p className="costs-detail-error-title">Could not load application drill-down</p>
          <pre className="costs-error-detail">{errorText}</pre>
          <button type="button" className="costs-refresh-btn" onClick={onRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (month === undefined) {
    return (
      <div className="costs-app-detail-panel" id={panelId} role="region">
        <p className="costs-detail-empty">No drill-down payload returned.</p>
      </div>
    );
  }

  const cy = month.currency || summaryRow.currency;

  return (
    <div className="costs-app-detail-panel" id={panelId} role="region">
      <DedicatedAppResourcesSection month={month} currency={cy} />

      <h4 className="costs-detail-subheading">Shared allocation</h4>
      {month.sharedAllocationSources.length === 0 ? (
        <p className="costs-detail-empty">No shared pool allocations attributed to this app.</p>
      ) : (
        <table className="costs-phase2-table costs-detail-subtable">
          <thead>
            <tr>
              <th scope="col">Shared pool</th>
              <th scope="col" className="costs-table-num">
                Allocated to app
              </th>
              <th scope="col" className="costs-table-num">
                % of pool
              </th>
            </tr>
          </thead>
          <tbody>
            {month.sharedAllocationSources.map((src) => (
              <tr key={src.sharedCategory}>
                <th scope="row">{src.sharedCategory}</th>
                <td className="costs-table-num">
                  {formatCurrency(src.allocatedAmount, cy)}
                </td>
                <td className="costs-table-num">{src.percentOfSharedCategory.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Inline detail row showing how much of a shared bucket's cost came from configured
 * usage-type rules vs proportional fallback. Rendered inside each shared-card when
 * `allocationBreakdown` is present on the `SharedCostAllocation`.
 */
function AllocationBreakdownDetail({
  breakdown,
  currency,
}: {
  breakdown: AllocationBreakdown;
  currency: string;
}): JSX.Element {
  return (
    <dl className="costs-alloc-breakdown">
      <dt>From configured rules</dt>
      <dd>{formatCurrency(breakdown.amountFromConfiguredRules, currency)}</dd>
      <dt>From proportional split</dt>
      <dd>{formatCurrency(breakdown.amountFromProportionalSplit, currency)}</dd>
      <dt>Rules version</dt>
      <dd>v{breakdown.sharedAllocationRulesVersion} · {breakdown.usageTypeRuleCount} usage-type rule{breakdown.usageTypeRuleCount !== 1 ? 's' : ''}</dd>
    </dl>
  );
}

/**
 * Attribution callout placed just above the untagged resources table.
 * Rendered only when `ctx` is present (newer API responses).
 * Shows which shared buckets used config-driven rules and notes that the remainder
 * falls back to proportional allocation.
 */
function SharedAttributionCallout({
  ctx,
}: {
  ctx: SharedCostAttributionContext | undefined;
}): JSX.Element | null {
  if (ctx === undefined) {
    return null;
  }

  const hasConfigRules = ctx.sharedBucketsUsingConfiguredRules.length > 0;

  return (
    <div
      className={`costs-attribution-callout${hasConfigRules ? ' costs-attribution-callout--config' : ' costs-attribution-callout--proportional'}`}
      role="note"
      aria-label="Shared allocation attribution"
    >
      <p className="costs-attribution-title">
        <span className={`badge ${hasConfigRules ? 'badge-info' : 'badge-neutral'}`}>
          {hasConfigRules ? 'Config rules applied' : 'Proportional allocation only'}
        </span>
        {' '}Shared allocation attribution
      </p>
      {hasConfigRules ? (
        <>
          <p className="costs-attribution-body">
            The following shared bucket{ctx.sharedBucketsUsingConfiguredRules.length !== 1 ? 's' : ''}{' '}
            used config-driven usage-type rules this month (rules version v{ctx.sharedAllocationRulesVersion},{' '}
            {ctx.usageTypeRuleCount} rule{ctx.usageTypeRuleCount !== 1 ? 's' : ''}):
          </p>
          <ul className="costs-attribution-chips" aria-label="Shared buckets using configured rules">
            {ctx.sharedBucketsUsingConfiguredRules.map((bucket) => (
              <li key={bucket} className="costs-attribution-chip">{bucket}</li>
            ))}
          </ul>
          <p className="costs-attribution-body costs-attribution-body--secondary">
            All remaining shared allocation uses proportional fallback.
          </p>
        </>
      ) : (
        <p className="costs-attribution-body">
          All shared cost allocation this month uses proportional allocation — no configured
          usage-type rules matched (rules version v{ctx.sharedAllocationRulesVersion},{' '}
          {ctx.usageTypeRuleCount} rule{ctx.usageTypeRuleCount !== 1 ? 's' : ''} checked).
        </p>
      )}
    </div>
  );
}

export function CostsByAppTab(): JSX.Element {
  const { mode, manualRefreshNonce, tabVisible, requestManualRefresh } = useRefresh();
  const [byApp, setByApp] = useState<CostsByAppResponse | null>(null);
  const [untagged, setUntagged] = useState<UntaggedResourcesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /** Expanded application key from `CostByApp.app` (matches GET /costs/by-app/{app}). */
  const [expandedAppKey, setExpandedAppKey] = useState<string | null>(null);
  const [detailByApp, setDetailByApp] = useState<Partial<Record<string, AppCostDetailResponse>>>(
    {},
  );
  const [detailErrorByApp, setDetailErrorByApp] = useState<Partial<Record<string, string>>>({});

  useLayoutEffect(() => {
    setDetailByApp({});
    setDetailErrorByApp({});
  }, [manualRefreshNonce]);

  useEffect(() => {
    if (!tabVisible) {
      return undefined;
    }

    let cancelled = false;

    function run(silent: boolean): void {
      if (cancelled) {
        return;
      }
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }

      void Promise.allSettled([getCostsByApp(), getUntaggedResources()])
        .then((results) => {
          if (cancelled) {
            return;
          }
          const errParts: string[] = [];

          const r0 = results[0];
          if (r0?.status === 'fulfilled') {
            setByApp(r0.value);
          } else {
            errParts.push(formatPhase2LoadError(r0.reason, 'GET /costs/by-app'));
          }

          const r1 = results[1];
          if (r1?.status === 'fulfilled') {
            setUntagged(r1.value);
          } else {
            errParts.push(formatPhase2LoadError(r1.reason, 'GET /costs/untagged'));
          }

          if (errParts.length > 0) {
            setError(errParts.join('\n\n—\n\n'));
          } else {
            setError(null);
          }
        })
        .finally(() => {
          if (!cancelled && !silent) {
            setIsLoading(false);
          }
        });
    }

    run(false);

    let intervalId: number | undefined;
    if (mode === 'auto') {
      intervalId = window.setInterval(() => run(true), POLL_INTERVAL_COSTS_MS);
    }

    return () => {
      cancelled = true;
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [mode, tabVisible, manualRefreshNonce]);

  useEffect(() => {
    if (expandedAppKey === null) {
      return undefined;
    }
    if (detailByApp[expandedAppKey] !== undefined) {
      return undefined;
    }
    if (detailErrorByApp[expandedAppKey] !== undefined) {
      return undefined;
    }

    let cancelled = false;
    const appKey = expandedAppKey;

    void (async (): Promise<void> => {
      try {
        const detail = await getAppCostDetail(appKey);
        if (!cancelled) {
          setDetailByApp((prev) => ({ ...prev, [appKey]: detail }));
        }
      } catch (err) {
        if (!cancelled) {
          setDetailErrorByApp((prev) => ({
            ...prev,
            [appKey]: formatPhase2LoadError(
              err,
              `GET /costs/by-app/${encodeURIComponent(appKey)}`,
            ),
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [expandedAppKey, detailByApp, detailErrorByApp]);

  const toggleExpanded = useCallback((appKey: string): void => {
    setExpandedAppKey((current) => {
      if (current === appKey) {
        return null;
      }
      return appKey;
    });
  }, []);

  const retryDetailFetch = useCallback((appKey: string): void => {
    setDetailByApp((prev) => {
      const next = { ...prev };
      delete next[appKey];
      return next;
    });
    setDetailErrorByApp((prev) => {
      const next = { ...prev };
      delete next[appKey];
      return next;
    });
  }, []);

  const currency = byApp?.currentMonth.currency ?? untagged?.currency ?? 'USD';
  const sortedCurrentApps = useMemo(
    () => (byApp !== null ? sortAppsByTotal(byApp.currentMonth.costByApp) : []),
    [byApp],
  );
  const sortedPreviousApps = useMemo(
    () => (byApp !== null ? sortAppsByTotal(byApp.previousMonth.costByApp) : []),
    [byApp],
  );

  const lastUpdated = useMemo(() => {
    if (byApp === null && untagged === null) {
      return null;
    }
    if (byApp === null) {
      return untagged?.lastUpdated ?? null;
    }
    if (untagged === null) {
      return byApp.lastUpdated;
    }
    return new Date(byApp.lastUpdated) >= new Date(untagged.lastUpdated)
      ? byApp.lastUpdated
      : untagged.lastUpdated;
  }, [byApp, untagged]);

  return (
    <div className="costs-subpanel">
      <div className="costs-toolbar" role="group" aria-label="Blooph application costs refresh">
        {!isLoading && lastUpdated !== null && (
          <p className="costs-last-updated">Last updated {formatWhen(lastUpdated)}</p>
        )}
        <button
          type="button"
          className="costs-refresh-btn"
          onClick={(): void => requestManualRefresh()}
          disabled={isLoading}
        >
          Refresh
        </button>
      </div>

      {isLoading && <p className="placeholder">Loading application costs…</p>}

      {!isLoading && error !== null && byApp === null && untagged === null && (
        <div className="costs-error" role="alert">
          <p className="costs-error-title">Could not load Phase 2 cost data</p>
          <pre className="costs-error-detail">{error}</pre>
        </div>
      )}

      {!isLoading && error !== null && (byApp !== null || untagged !== null) && (
        <div className="costs-phase2-partial-banner" role="status">
          <p className="costs-phase2-partial-title">Partial load — one or more requests failed</p>
          <pre className="costs-error-detail">{error}</pre>
        </div>
      )}

      {!isLoading && (byApp !== null || untagged !== null) && (
        <>
          <section className="costs-summary-grid" aria-label="Application cost summary">
            {byApp !== null ? (
              <>
                <article className="costs-summary-card">
                  <h3 className="costs-card-title">Current month (by app)</h3>
                  <p className="costs-card-value">
                    {formatCurrency(byApp.currentMonth.totalCost, currency)}
                  </p>
                  <p className="costs-card-meta">
                    {formatShortDate(byApp.currentMonth.periodStart)} –{' '}
                    {formatShortDate(byApp.currentMonth.periodEnd)} (month to date)
                  </p>
                </article>
                <article className="costs-summary-card">
                  <h3 className="costs-card-title">Previous month (by app)</h3>
                  <p className="costs-card-value">
                    {formatCurrency(byApp.previousMonth.totalCost, currency)}
                  </p>
                  <p className="costs-card-meta">
                    {formatShortDate(byApp.previousMonth.periodStart)} –{' '}
                    {formatShortDate(byApp.previousMonth.periodEnd)}
                  </p>
                </article>
              </>
            ) : (
              <article className="costs-summary-card">
                <h3 className="costs-card-title">By application</h3>
                <p className="costs-card-value">—</p>
                <p className="costs-card-meta">GET /costs/by-app did not return data.</p>
              </article>
            )}
            {untagged !== null ? (
              <article className="costs-summary-card">
                <h3 className="costs-card-title">Untagged (MTD)</h3>
                <p className="costs-card-value">
                  {formatCurrency(untagged.totalCost, untagged.currency || currency)}
                </p>
                <p className="costs-card-meta">From GET /costs/untagged</p>
              </article>
            ) : (
              <article className="costs-summary-card">
                <h3 className="costs-card-title">Untagged (MTD)</h3>
                <p className="costs-card-value">—</p>
                <p className="costs-card-meta">GET /costs/untagged did not return data.</p>
              </article>
            )}
          </section>

          {byApp !== null ? (
            <section className="costs-section" aria-labelledby="costs-apps-current-heading">
              <h3 id="costs-apps-current-heading" className="costs-section-title">
                By application — current month
              </h3>
              <div className="costs-table-wrap">
                <table className="costs-phase2-table">
                <thead>
                  <tr>
                    <th scope="col">Application</th>
                    <th scope="col" className="costs-table-num">
                      Dedicated
                    </th>
                    <th scope="col" className="costs-table-num">
                      Allocated (shared)
                    </th>
                    <th scope="col" className="costs-table-num">
                      Total
                    </th>
                    <th scope="col" className="costs-table-num">
                      % of total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCurrentApps.map((row) => {
                    const isOpen = expandedAppKey === row.app;
                    const panelId = panelDomId(row.app);
                    const btnId = `costs-app-toggle-${encodeURIComponent(row.app).replace(/%/g, '')}`;
                    return (
                      <Fragment key={row.app}>
                        <tr className={isOpen ? 'costs-app-row-expanded' : undefined}>
                          <th scope="row">
                            <button
                              type="button"
                              id={btnId}
                              className="costs-app-expand-btn"
                              aria-expanded={isOpen}
                              aria-controls={panelId}
                              onClick={(): void => toggleExpanded(row.app)}
                            >
                              <span
                                className={`costs-expand-chevron${isOpen ? ' is-open' : ''}`}
                                aria-hidden
                              />
                              <span>{appDisplayName(row.app)}</span>
                            </button>
                          </th>
                          <td className="costs-table-num">
                            {formatCurrency(row.dedicatedCost, row.currency)}
                          </td>
                          <td className="costs-table-num">
                            {formatCurrency(row.allocatedSharedCost, row.currency)}
                          </td>
                          <td className="costs-table-num costs-table-strong">
                            {formatCurrency(row.totalCost, row.currency)}
                          </td>
                          <td className="costs-table-num">{row.percentOfTotal.toFixed(1)}%</td>
                        </tr>
                        {isOpen ? (
                          <tr className="costs-app-detail-row">
                            <td colSpan={5}>
                              <AppCostDetailPanel
                                summaryRow={row}
                                detailResp={detailByApp[row.app]}
                                loading={
                                  detailByApp[row.app] === undefined &&
                                  detailErrorByApp[row.app] === undefined
                                }
                                errorText={detailErrorByApp[row.app]}
                                onRetry={(): void => retryDetailFetch(row.app)}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
          ) : null}

          {byApp !== null ? (
            <section className="costs-section" aria-labelledby="costs-apps-prev-heading">
              <h3 id="costs-apps-prev-heading" className="costs-section-title">
                By application — previous month
              </h3>
              <div className="costs-table-wrap">
                <table className="costs-phase2-table">
                <thead>
                  <tr>
                    <th scope="col">Application</th>
                    <th scope="col" className="costs-table-num">
                      Dedicated
                    </th>
                    <th scope="col" className="costs-table-num">
                      Allocated (shared)
                    </th>
                    <th scope="col" className="costs-table-num">
                      Total
                    </th>
                    <th scope="col" className="costs-table-num">
                      % of total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPreviousApps.map((row) => (
                    <tr key={`prev-${row.app}`}>
                      <th scope="row">{appDisplayName(row.app)}</th>
                      <td className="costs-table-num">
                        {formatCurrency(row.dedicatedCost, row.currency)}
                      </td>
                      <td className="costs-table-num">
                        {formatCurrency(row.allocatedSharedCost, row.currency)}
                      </td>
                      <td className="costs-table-num costs-table-strong">
                        {formatCurrency(row.totalCost, row.currency)}
                      </td>
                      <td className="costs-table-num">{row.percentOfTotal.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          ) : null}

          {byApp !== null && byApp.currentMonth.sharedAllocations.length > 0 ? (
            <section className="costs-section" aria-labelledby="costs-shared-heading">
              <h3 id="costs-shared-heading" className="costs-section-title">
                Shared cost allocation (current month)
              </h3>
              <ul className="costs-shared-list">
                {byApp.currentMonth.sharedAllocations.map((block) => (
                  <li key={block.sharedCategory} className="costs-shared-card">
                    <h4 className="costs-shared-title">{block.sharedCategory}</h4>
                    <p className="costs-shared-meta">
                      {formatCurrency(block.totalCost, currency)} · {block.allocationMethod}
                    </p>
                    <table className="costs-phase2-table costs-shared-subtable">
                      <thead>
                        <tr>
                          <th scope="col">Application</th>
                          <th scope="col" className="costs-table-num">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {block.allocations.map((a) => (
                          <tr key={`${block.sharedCategory}-${a.app}`}>
                            <th scope="row">{appDisplayName(a.app)}</th>
                            <td className="costs-table-num">{formatCurrency(a.amount, currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {block.allocationBreakdown !== undefined ? (
                      <AllocationBreakdownDetail
                        breakdown={block.allocationBreakdown}
                        currency={currency}
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {untagged !== null ? (
            <section className="costs-section" aria-labelledby="costs-untagged-heading">
              <h3 id="costs-untagged-heading" className="costs-section-title">
                Untagged resources (month to date)
              </h3>
              <SharedAttributionCallout ctx={untagged.sharedCostAttributionContext} />
              <p className="costs-pie-lead">
                Rows are Cost Explorer billable resources without a{' '}
                <code className="costs-pie-inline-code">blooph-cost-app</code> tag. Some rows are <strong>ARNs</strong>{' '}
                (Name / match shows a short id; Region · account comes from the ARN; use Copy ID / Open). Others are{' '}
                <strong>usage or dimension strings</strong> (for example Fargate vCPU-hours) — those are not taggable
                directly; follow the hint under Name / match and tag the underlying ECS or capacity. When{' '}
                <code className="costs-pie-inline-code">GET /costs/untagged</code> returns <code>tags</code> on a row,
                they appear in the Tags column to help identify the resource.
              </p>
              {untagged.resources.length === 0 ? (
                <p className="empty-state" role="status">
                  No untagged resources returned for this period.
                </p>
              ) : (
                <div className="costs-table-wrap">
                  <table className="costs-phase2-table">
                    <thead>
                      <tr>
                        <th scope="col">Name / match</th>
                        <th scope="col">Region · account</th>
                        <th scope="col">Tags</th>
                        <th scope="col">Resource ID</th>
                        <th scope="col">Type</th>
                        <th scope="col">AWS service</th>
                        <th scope="col" className="costs-table-num">
                          Cost (MTD)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {untagged.resources.map((r: UntaggedResource) => {
                        const parsed = parseUntaggedResourceId(r.resourceId);
                        const nameDisp = getUntaggedNameDisplay(
                          r.resourceId,
                          parsed,
                          r.displayName,
                        );
                        const tagRows = sortedUntaggedTagEntries(r.tags);
                        const primaryTitle =
                          nameDisp.hint === null && nameDisp.hintDetail !== null
                            ? nameDisp.hintDetail
                            : undefined;
                        return (
                          <tr key={`${r.resourceId}-${r.awsService}`}>
                            <td>
                              <div className="costs-untagged-name" title={primaryTitle}>
                                {nameDisp.primary}
                              </div>
                              {nameDisp.hint !== null ? (
                                <p
                                  className="costs-untagged-hint"
                                  title={nameDisp.hintDetail ?? nameDisp.hint}
                                >
                                  {nameDisp.hint}
                                </p>
                              ) : null}
                              {parsed.consoleUrl !== null ? (
                                <a
                                  className="costs-untagged-console-link"
                                  href={parsed.consoleUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Open
                                </a>
                              ) : null}
                              {r.matchingAllocationRuleId !== undefined ? (
                                <span className="badge badge-info costs-untagged-rule-badge">
                                  Auto-attributed · rule: {r.matchingAllocationRuleId}
                                </span>
                              ) : null}
                            </td>
                            <td className="costs-table-mono costs-untagged-scope">
                              {formatUntaggedRegionCell(r.resourceId, parsed)}
                            </td>
                            <td className="costs-untagged-tags-cell">
                              {tagRows.length > 0 ? (
                                <ul className="costs-untagged-tag-list">
                                  {tagRows.map(([k, v]) => (
                                    <li key={`${r.resourceId}-${k}`}>
                                      <code className="costs-untagged-tag-key">{k}</code>
                                      <span className="costs-untagged-tag-sep">=</span>
                                      <span className="costs-untagged-tag-val">{v}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="costs-untagged-tags-empty">—</span>
                              )}
                            </td>
                            <td className="costs-untagged-id-cell">
                              <div className="costs-table-mono costs-untagged-id">{r.resourceId}</div>
                              <CopyUntaggedResourceIdButton resourceId={r.resourceId} />
                            </td>
                            <td>{r.resourceType}</td>
                            <td>{r.awsService}</td>
                            <td className="costs-table-num">
                              {formatCurrency(r.monthlyCost, r.currency || untagged.currency || currency)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
